import AccountAccessPresenter from "./AccountAccessPresenter";
import {
  AdminUserResult,
  ProfileAccountInfoFromServer,
} from "api/interfaces/accountInterface.interface";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import { useMenu } from "hook/useMenu";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ACCESS_TYPE,
  ACCESS_TYPE_ORDER,
  isAccessTypeDisabled,
  isAccessTypeVisible,
} from "./AccountAccessUtil";
import { useQuery } from "react-query";
import { getAccountAdminUsers } from "api/accountAPI";
import { Option } from "utils/options";
import {
  RequestTemporaryBody,
  RequestTemporaryResponse,
} from "api/interfaces/userInterface.interface";
import useStatusMutation from "hook/useStatusMutation";
import { getUserDetailAccount, postRequestTemporary } from "api/userAPI";
import useApiError from "hook/useApiError";
import { changeToAccountFormat } from "redux/reducers/account/accountSlice";
import { CustomError } from "api/interfaces/commonInterface.interface";
import useStompDebouncedRefetch from "hook/useStompDebouncedRefetch";
import { setAxiosHeaders } from "utils/AuthUtil";

export interface Props {
  accountId: string;
  onModal: () => void;
  useTemporary: boolean;
  isShowOnlyUser: boolean;
  toPath?: string;
  atTopLevel: boolean;
}

export default function AccountAccess({
  accountId,
  atTopLevel,
  onModal,
  useTemporary,
  isShowOnlyUser,
  toPath,
}: Props): JSX.Element {
  const { handleErrorNotify, lang, notifySuccess } = useApiError();
  const { navigateSwitchAccount } = useMenu({});

  const {
    data,
    isLoading: isLoadingFormatAccount,
    refetch,
  } = useQuery(
    ["selectAccount", accountId],
    () => {
      return getUserDetailAccount(accountId);
    },
    {
      retry: 0,
      refetchOnWindowFocus: false,
      enabled: !!accountId,
    }
  );
  useStompDebouncedRefetch({
    stompKey: "loginAccessUpdated",
    refetch: refetch,
  });

  const formatAccount = useMemo(() => {
    return data?.accounts
      ? changeToAccountFormat(data?.accounts as ProfileAccountInfoFromServer)
      : null;
  }, [data]);

  const selectableAccessTypes = useMemo(() => {
    if (formatAccount) {
      return ACCESS_TYPE_ORDER.filter(
        (accessType) =>
          isAccessTypeVisible(formatAccount.accountLevel, accessType) &&
          !isAccessTypeDisabled(formatAccount, accessType)
      );
    }
    return [];
  }, [formatAccount]);

  const [selectedAccessType, setSelectedAccessType] = useState<ACCESS_TYPE>(
    selectableAccessTypes[0]
  );

  const [requestInfo, setRequestInfo] = useState<RequestTemporaryBody>({
    recipient: "",
    duration: -1,
  });

  const [validateMsg, setValidateMsg] = useState<Record<string, string>>({
    recipient: "",
    duration: "",
  });
  const { data: adminUsers, isFetching } = useQuery(
    ["accountAdminUsers", formatAccount?.accountId],
    () =>
      getAccountAdminUsers({
        accountId: formatAccount?.accountId as string,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      enabled:
        !!formatAccount?.accountId &&
        formatAccount?.accountLevel === LEVEL_TYPE.EU,
    }
  );

  const mutationRequestTmpAccess = useStatusMutation(
    () =>
      postRequestTemporary({
        payload: requestInfo,
        accountId: formatAccount?.accountId as string,
      }),
    {
      onSuccess: (res: RequestTemporaryResponse) => {
        if (res.error === 0) {
          notifySuccess(lang.validateMsg.account.access.temporary.success);
          onModal();
          return;
        }
      },
      onError: (err: CustomError) => {
        handleErrorNotify(err, lang.validateMsg.account.access.temporary.fail);
      },
    }
  );

  const adminUserOptions: Option[] = useMemo(() => {
    if (adminUsers?.result) {
      return adminUsers.result.map((user: AdminUserResult) => {
        return {
          label: user.email,
          value: user.email,
        };
      });
    }
    return [];
  }, [adminUsers]);

  useEffect(() => {
    if (!selectableAccessTypes.includes(selectedAccessType)) {
      setSelectedAccessType(selectableAccessTypes[0]);
    }
  }, [selectedAccessType, selectableAccessTypes]);

  const onChangeAccessType = (value: ACCESS_TYPE) => {
    if (selectableAccessTypes.includes(value)) {
      setSelectedAccessType(value);
    }
  };

  const onChangeRecipient = useCallback(
    ({ name, value }: { name: string; value: string | number }) => {
      if (name === "recipient" || name === "duration") {
        setSelectedAccessType(ACCESS_TYPE.TEMPORARY);
      }
      setRequestInfo((info) => {
        return {
          ...info,
          [name]: value,
        };
      });
    },
    [requestInfo]
  );

  const onClickOk = useCallback(
    async (accessType: ACCESS_TYPE = selectedAccessType) => {
      if (accessType === ACCESS_TYPE.TEMPORARY) {
        if (
          requestInfo.recipient !== "" &&
          requestInfo.duration !== -1 &&
          !!formatAccount?.accountId
        ) {
          mutationRequestTmpAccess.mutate();
        }

        if (requestInfo.recipient === "") {
          setValidateMsg((msg) => {
            return {
              ...msg,
              recipient:
                lang.validateMsg.account.access.temporary.validateMsg.recipient,
            };
          });
          return;
        }

        if (requestInfo.duration === -1) {
          setValidateMsg((msg) => {
            return {
              ...msg,
              duration:
                lang.validateMsg.account.access.temporary.validateMsg.duration,
            };
          });
          return;
        }
        onModal();
        return;
      }
      const isHidden =
        accessType === ACCESS_TYPE.MANAGED ||
        formatAccount?.userPermission.groupName?.toUpperCase() === "HIDDEN";
      navigateSwitchAccount(
        {
          accountId: accountId,
          onSuccess: () => {
            onModal();
          },
          pathname: toPath,
        },
        isHidden
      );
    },
    [
      selectedAccessType,
      formatAccount?.userPermission.groupName,
      formatAccount?.accountId,
      accountId,
      navigateSwitchAccount,
      toPath,
      onModal,
      requestInfo.recipient,
      requestInfo.duration,
      mutationRequestTmpAccess,
      lang.validateMsg.account.access.temporary.validateMsg.recipient,
      lang.validateMsg.account.access.temporary.validateMsg.duration,
    ]
  );
  const isShowModal = useMemo(() => {
    if (!accountId) return false;
    if (!data) return false;
    if (isLoadingFormatAccount) return false;
    if (!formatAccount) return false;
    if (useTemporary === false && isShowOnlyUser === true) {
      if (atTopLevel) {
        return false;
      }
      if (formatAccount?.userPermission.groupName?.toUpperCase() === "HIDDEN") {
        return false;
      }
      return true;
    }
    return true;
  }, [
    accountId,
    data,
    isLoadingFormatAccount,
    formatAccount,
    useTemporary,
    isShowOnlyUser,
    atTopLevel,
  ]);

  useEffect(() => {
    if (requestInfo.recipient !== "" && requestInfo.duration !== -1) {
      setValidateMsg({
        recipient: "",
        duration: "",
      });
    }
  }, [requestInfo]);
  useEffect(() => {
    if (!accountId) return;
    if (!formatAccount?.accountId) return;
    if (!formatAccount?.isHidden) {
      setSelectedAccessType(ACCESS_TYPE.REGISTERED);
    }
    if (useTemporary === false && isShowOnlyUser === true) {
      if (formatAccount?.userPermission.groupName?.toUpperCase() === "HIDDEN") {
        onClickOk(ACCESS_TYPE.MANAGED);
      } else if (atTopLevel) {
        onClickOk(ACCESS_TYPE.REGISTERED);
      }
    }
    // onClickOk는 useCallback으로 자주 재생성되어 중복 navigate를 유발하므로 의존성에서 제외
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    useTemporary,
    isShowOnlyUser,
    formatAccount?.userPermission?.groupName,
    accountId,
    formatAccount?.accountId,
    atTopLevel,
  ]);
  const onCloseModal = () => {
    setAxiosHeaders();
    onModal();
  };
  if (!isShowModal || !formatAccount) return <></>;
  return (
    <AccountAccessPresenter
      onModal={onCloseModal}
      formatAccount={formatAccount}
      selectedAccessType={selectedAccessType}
      onChangeAccessType={onChangeAccessType}
      adminUserOptions={adminUserOptions}
      requestInfo={requestInfo}
      onChangeRecipient={onChangeRecipient}
      onClickOk={onClickOk}
      validateMsg={validateMsg}
      useTemporary={useTemporary ?? false}
      isShowOnlyUser={isShowOnlyUser ?? false}
      adminUserIsFetching={isFetching}
    />
  );
}
